// src/hooks/usePipsCreatureKeeper.ts
// FIXED: Preserve selectedHabitatIndex when reloading data

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { Game, GameData, Creature } from '../types/pipsCreatureKeeper';
import {
  createGame,
  loadGame,
  loadGameData,
  addStarterCreature,
  addToFieldJournal,
  deleteGame
} from '../services/pipsCreatureKeeperService';
import { loadHabitatPlacements, updateAllHappiness } from '../services/pipsCreatureKeeperHabitatService';
import { checkAndUnlockHabitats, checkAndUnlockTerritories } from '../services/pipsCreatureKeeperTerritoryService';
import { logger } from '../utils/logger';
import { soundService } from '../services/soundService';

export const usePipsCreatureKeeper = () => {
  const [currentGame, setCurrentGame] = useState<Game | null>(null);
  const [gameData, setGameData] = useState<GameData>({
    creatures: [],
    territories: [],
    habitats: [],
    myCreatures: [],
    fieldJournal: [],
    typeCompatibility: [],
    creatureTerritories: [],
    selectedCompanion: null,
    habitatPlacements: {},
    unlockedHabitats: [],
    unlockedTerritories: [],
    selectedHabitatIndex: 0
  });
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Auth + initial load
  useEffect(() => {
    checkAuthAndLoadGame();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUserId(session.user.id);
        await loadExistingGame(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        resetGameState();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const resetGameState = () => {
    setUserId(null);
    setCurrentGame(null);
    setGameData({
      creatures: [],
      territories: [],
      habitats: [],
      myCreatures: [],
      fieldJournal: [],
      typeCompatibility: [],
      creatureTerritories: [],
      selectedCompanion: null,
      habitatPlacements: {},
      unlockedHabitats: [],
      unlockedTerritories: [],
      selectedHabitatIndex: 0
    });
  };

  const checkAuthAndLoadGame = useCallback(async () => {
    setLoading(true);
    const {
      data: { session }
    } = await supabase.auth.getSession();

    if (session?.user) {
      setUserId(session.user.id);
      await loadExistingGame(session.user.id);
    }
    setLoading(false);
  }, []);

  const loadExistingGame = useCallback(async (userId: string, preserveHabitatIndex?: boolean, onProgress?: (step: string) => void) => {
    onProgress?.('Loading game...');
    const game = await loadGame(userId);
    if (!game) {
      setCurrentGame(null);
      return;
    }

    setCurrentGame(game);
    onProgress?.('Loading creatures and territories...');
    const data = await loadGameData(game.id);

    // Load habitat placements
    onProgress?.('Loading habitats...');
    const placements = loadHabitatPlacements(data);

    // Convert single highest unlocked ID to array of all unlocked IDs
    const maxHabitatId = Array.isArray(data.unlockedHabitats)
      ? Math.max(...data.unlockedHabitats)
      : data.unlockedHabitats || 0;
    const allUnlockedHabitats = maxHabitatId > 0
      ? Array.from({ length: maxHabitatId }, (_, i) => i + 1)
      : [];

    const maxTerritoryId = Array.isArray(data.unlockedTerritories)
      ? Math.max(...data.unlockedTerritories)
      : data.unlockedTerritories || 0;
    const allUnlockedTerritories = maxTerritoryId > 0
      ? Array.from({ length: maxTerritoryId }, (_, i) => i + 1)
      : [];

    // Check if we need to unlock more habitats based on current creature count
    const updatedData = {
      ...data,
      unlockedHabitats: allUnlockedHabitats,
      unlockedTerritories: allUnlockedTerritories
    };

    onProgress?.('Checking for new unlocks...');
    logger.log('Checking if habitats need unlocking on load...');
    const newHabitatUnlocks = await checkAndUnlockHabitats(game.id, updatedData);
    if (newHabitatUnlocks.length > 0) {
      logger.log('Auto-unlocked habitats on load:', newHabitatUnlocks);
      // Reload to get the updated unlocked habitats
      onProgress?.('Loading updated data...');
      const reloadedData = await loadGameData(game.id);
      const reloadedMaxHabitatId = Array.isArray(reloadedData.unlockedHabitats)
        ? Math.max(...reloadedData.unlockedHabitats)
        : reloadedData.unlockedHabitats || 0;
      const reloadedUnlockedHabitats = reloadedMaxHabitatId > 0
        ? Array.from({ length: reloadedMaxHabitatId }, (_, i) => i + 1)
        : [];

      setGameData(prev => ({
        ...reloadedData,
        habitatPlacements: placements,
        unlockedHabitats: reloadedUnlockedHabitats,
        unlockedTerritories: allUnlockedTerritories,
        selectedHabitatIndex: preserveHabitatIndex ? prev.selectedHabitatIndex : 0
      }));
    } else {
      setGameData(prev => ({
        ...data,
        habitatPlacements: placements,
        unlockedHabitats: allUnlockedHabitats,
        unlockedTerritories: allUnlockedTerritories,
        selectedHabitatIndex: preserveHabitatIndex ? prev.selectedHabitatIndex : 0
      }));
    }

    // Calculate initial happiness after loading placements
    onProgress?.('Calculating happiness...');
    logger.log('Calculating initial happiness for all habitats...');
    await updateAllHappiness(placements, data.creatures, data.typeCompatibility);
    logger.log('Initial happiness calculated');
    onProgress?.('Complete!');
  }, []);

  const startNewGame = useCallback(
    async (playerName: string) => {
      if (!userId) throw new Error('User not authenticated');
      const newGame = await createGame(userId, playerName);
      setCurrentGame(newGame);
      const data = await loadGameData(newGame.id);

      // Load habitat placements
      const placements = loadHabitatPlacements(data);

      // Convert single highest unlocked ID to array of all unlocked IDs
      const maxHabitatId = Array.isArray(data.unlockedHabitats)
        ? Math.max(...data.unlockedHabitats)
        : data.unlockedHabitats || 0;
      const allUnlockedHabitats = maxHabitatId > 0
        ? Array.from({ length: maxHabitatId }, (_, i) => i + 1)
        : [];

      const maxTerritoryId = Array.isArray(data.unlockedTerritories)
        ? Math.max(...data.unlockedTerritories)
        : data.unlockedTerritories || 0;
      const allUnlockedTerritories = maxTerritoryId > 0
        ? Array.from({ length: maxTerritoryId }, (_, i) => i + 1)
        : [];

      setGameData({
        ...data,
        habitatPlacements: placements,
        unlockedHabitats: allUnlockedHabitats,
        unlockedTerritories: allUnlockedTerritories,
        selectedHabitatIndex: 0
      });
    },
    [userId]
  );

  // alias for backwards compatibility
  const createNewGame = startNewGame;

  const onCreatureCaptured = useCallback(
    async (creature: Creature) => {
      if (!currentGame) return;

      logger.log('Creature captured, checking unlocks...');
      await addToFieldJournal(currentGame.id, creature.id);

      // Important: We need to check with the NEW count (including the just-captured creature)
      const newCreatureCount = gameData.myCreatures.length + 1;
      logger.log(`Creature count after capture: ${newCreatureCount}`);

      // Create updated gameData with new count for unlock checks
      const updatedGameData = {
        ...gameData,
        myCreatures: [...gameData.myCreatures, { creature_id: creature.id } as any]
      };

      // Check for new unlocks with updated count
      const newTerritoryUnlocks = await checkAndUnlockTerritories(currentGame.id, updatedGameData);
      const newHabitatUnlocks = await checkAndUnlockHabitats(currentGame.id, updatedGameData);

      if (newTerritoryUnlocks.length > 0) {
        logger.log('New territories unlocked:', newTerritoryUnlocks);
        soundService.play('sanctuary-unlock', { volume: 0.7, haptic: 'success' });
      }
      if (newHabitatUnlocks.length > 0) {
        logger.log('New habitats unlocked:', newHabitatUnlocks);
        soundService.play('sanctuary-unlock', { volume: 0.7, haptic: 'success' });
      }

      // Reload full game data to get updated unlocks, but preserve habitat index
      logger.log('Reloading game data after capture...');
      await loadExistingGame(currentGame.user_id, true);
    },
    [currentGame, gameData, loadExistingGame]
  );

  const refreshHappiness = useCallback(async () => {
    if (!currentGame) return;
    await updateAllHappiness(
      gameData.habitatPlacements,
      gameData.creatures,
      gameData.typeCompatibility
    );
  }, [currentGame, gameData]);

  const reloadHabitatPlacements = useCallback(async () => {
    if (!currentGame) return;
    logger.log('Reloading habitat placements...');

    // Reload fresh game data to get updated placements
    const data = await loadGameData(currentGame.id);
    const placements = loadHabitatPlacements(data);

    logger.log('Fresh data loaded:', {
      myCreatures: data.myCreatures.length,
      unlockedHabitats: data.unlockedHabitats,
      placements: Object.keys(placements).length,
      placementDetails: placements
    });

    logger.log('MyCreatures details:', data.myCreatures.map(mc => ({
      id: mc.id.substring(0, 8),
      creature_id: mc.creature_id,
      habitat_id: mc.habitat_id,
      habitat_slot: mc.habitat_slot
    })));

    // Update BOTH myCreatures AND habitatPlacements, preserve habitat index
    setGameData(prev => {
      logger.log('Previous state:', {
        myCreatures: prev.myCreatures.length,
        placements: Object.keys(prev.habitatPlacements).length
      });

      return {
        ...prev,
        myCreatures: data.myCreatures,
        habitatPlacements: placements
      };
    });

    logger.log('State updated successfully');

    // Recalculate happiness with the NEW placements
    logger.log('Recalculating happiness after placement update...');
    await updateAllHappiness(placements, data.creatures, data.typeCompatibility);
    logger.log('Happiness recalculated');
  }, [currentGame]);

  const resetGame = useCallback(async () => {
    logger.log('Resetting game...');
    try {
      if (currentGame) {
        logger.log('Deleting game data from database...');
        await deleteGame(currentGame.id);
        logger.log('Game data deleted from database');
      }
      logger.log('Clearing local state...');
      resetGameState();

      // Restore userId from current session (user is still authenticated)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
        logger.log('UserId restored after reset');
      }

      logger.log('Game reset complete');
    } catch (error) {
      logger.error('Error resetting game:', error);
      // Still reset local state even if DB delete fails
      resetGameState();
      // Try to restore userId even on error
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
      throw error;
    }
  }, [currentGame]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    resetGameState();
  }, []);

  const refreshGameData = useCallback(async (onProgress?: (step: string) => void) => {
    if (currentGame) {
      logger.log('Refreshing all game data...');
      await loadExistingGame(currentGame.user_id, true, onProgress);
      logger.log('All game data refreshed');
    }
  }, [currentGame, loadExistingGame]);

  const selectStarter = useCallback(
    async (creatureId: number) => {
      if (!currentGame) return;
      await addStarterCreature(currentGame.id, creatureId);
      await refreshGameData();
    },
    [currentGame, refreshGameData]
  );

  const setSelectedCompanion = useCallback(
    (creature: Creature | null) => {
      setGameData(prev => ({ ...prev, selectedCompanion: creature }));
    },
    []
  );

  const setSelectedHabitatIndex = useCallback(
    (index: number) => {
      setGameData(prev => ({ ...prev, selectedHabitatIndex: index }));
    },
    []
  );

  return {
    userId,
    currentGame,
    gameData,
    loading,
    startNewGame,
    createNewGame,
    selectStarter,
    setSelectedCompanion,
    setSelectedHabitatIndex,
    refreshGameData,
    onCreatureCaptured,
    reloadHabitatPlacements,
    refreshHappiness,
    resetGame,
    signOut
  };
};